/**
 * Form defaults for the items create + edit flows (ADR-0013 redesign).
 *
 * Mirror of `features/customers/lib/form-defaults.ts`. RHF needs controlled
 * inputs (no `undefined`), so optional fields default to `''` on the form
 * side; the submit handlers strip empty optionals before round-tripping to
 * the API. Money is decimal rupees on the form and paise on the wire — the
 * `rupees ⇄ paise` conversion lives here so two routes don't reimplement it.
 *
 * V1.0.x added 18 new fields — see ADR-0013. Optional fields default to
 * `''` (strings/numbers), `[]` (arrays), or `false` (booleans).
 */
import type { items } from "@flint/api-contract";
import type { z } from "zod";

type ItemRow = z.infer<typeof items.itemRow>;
type ItemListRow = z.infer<typeof items.listRow>;
type ItemType = z.infer<typeof items.itemType>;
type ItemUsage = z.infer<typeof items.itemUsage>;
type TaxPreference = z.infer<typeof items.taxPreference>;
type AltUnit = z.infer<typeof items.altUnit>;

/**
 * The form's value shape. All non-undefined; submit handler strips
 * blank optionals.
 */
export interface ItemFormValues {
  // identity
  name: string;
  sku: string;
  description: string;
  itemType: ItemType;
  usage: ItemUsage;

  // categorisation
  category: string;
  brand: string;
  manufacturer: string;
  barcode: string;
  vendorSku: string;
  tags: string[];

  // pricing & GST
  hsn: string;
  unit: string;
  defaultRateRupees: string;
  /** Stored as the bps string ("1800" for 18%). */
  defaultTaxRateBpsStr: string;
  taxPreference: TaxPreference;
  cessRateBpsStr: string;
  lastCostRupees: string;
  mrpRupees: string;
  tdsSectionDefault: string;

  // inventory
  trackStock: boolean;
  reorderLevel: string;
  weightGrams: string;
  altUnits: AltUnit[];

  // vendor link
  preferredVendorId: string;
  leadTimeDays: string;
  minOrderQty: string;

  // media
  imageObjectKey: string;
}

export const EMPTY_FORM: ItemFormValues = {
  name: "",
  sku: "",
  description: "",
  itemType: "goods",
  usage: "both",

  category: "",
  brand: "",
  manufacturer: "",
  barcode: "",
  vendorSku: "",
  tags: [],

  hsn: "",
  unit: "Nos",
  defaultRateRupees: "",
  defaultTaxRateBpsStr: "",
  taxPreference: "taxable",
  cessRateBpsStr: "",
  lastCostRupees: "",
  mrpRupees: "",
  tdsSectionDefault: "",

  trackStock: true,
  reorderLevel: "",
  weightGrams: "",
  altUnits: [],

  preferredVendorId: "",
  leadTimeDays: "",
  minOrderQty: "",

  imageObjectKey: "",
};

/** Hydrate the form from a server-fetched item row (for edit). */
export function formFromItem(i: ItemRow): ItemFormValues {
  return {
    name: i.name,
    sku: i.sku ?? "",
    description: i.description ?? "",
    itemType: i.itemType,
    usage: i.usage,

    category: i.category ?? "",
    brand: i.brand ?? "",
    manufacturer: i.manufacturer ?? "",
    barcode: i.barcode ?? "",
    vendorSku: i.vendorSku ?? "",
    tags: i.tags,

    hsn: i.hsn ?? "",
    unit: i.unit,
    defaultRateRupees: paiseToRupees(i.defaultRatePaise),
    defaultTaxRateBpsStr: i.defaultTaxRateBps === null ? "" : i.defaultTaxRateBps.toString(),
    taxPreference: i.taxPreference,
    cessRateBpsStr: i.cessRateBps === null ? "" : i.cessRateBps.toString(),
    lastCostRupees: paiseToRupees(i.lastCostPaise),
    mrpRupees: paiseToRupees(i.mrpPaise),
    tdsSectionDefault: i.tdsSectionDefault ?? "",

    trackStock: i.trackStock,
    reorderLevel: i.reorderLevel === null ? "" : i.reorderLevel.toString(),
    weightGrams: i.weightGrams === null ? "" : i.weightGrams.toString(),
    altUnits: i.altUnits ?? [],

    preferredVendorId: i.preferredVendorId ?? "",
    leadTimeDays: i.leadTimeDays === null ? "" : i.leadTimeDays.toString(),
    minOrderQty: i.minOrderQty === null ? "" : i.minOrderQty.toString(),

    imageObjectKey: i.imageObjectKey ?? "",
  };
}

/** Hydrate the form from a list-page row (no full detail fetched). */
export function formFromListRow(i: ItemListRow): ItemFormValues {
  return {
    ...EMPTY_FORM,
    name: i.name,
    sku: i.sku ?? "",
    description: i.description ?? "",
    itemType: i.itemType,
    usage: i.usage,
    category: i.category ?? "",
    brand: i.brand ?? "",
    tags: i.tags,
    hsn: i.hsn ?? "",
    unit: i.unit,
    defaultRateRupees: paiseToRupees(i.defaultRatePaise),
    defaultTaxRateBpsStr: i.defaultTaxRateBps === null ? "" : i.defaultTaxRateBps.toString(),
    taxPreference: i.taxPreference,
    lastCostRupees: paiseToRupees(i.lastCostPaise),
    trackStock: i.trackStock,
    reorderLevel: i.reorderLevel === null ? "" : i.reorderLevel.toString(),
    preferredVendorId: i.preferredVendorId ?? "",
  };
}

/** Strip empty optionals + convert rupees → paise for the create payload. */
export function valuesToCreatePayload(v: ItemFormValues): z.infer<typeof items.createInput> {
  const out: z.infer<typeof items.createInput> = {
    name: v.name.trim(),
    itemType: v.itemType,
    usage: v.usage,
    taxPreference: v.taxPreference,
    trackStock: v.trackStock,
  };
  if (v.sku.trim()) out.sku = v.sku.trim();
  if (v.description.trim()) out.description = v.description.trim();
  if (v.category.trim()) out.category = v.category.trim();
  if (v.brand.trim()) out.brand = v.brand.trim();
  if (v.manufacturer.trim()) out.manufacturer = v.manufacturer.trim();
  if (v.barcode.trim()) out.barcode = v.barcode.trim();
  if (v.vendorSku.trim()) out.vendorSku = v.vendorSku.trim();
  if (v.tags.length > 0) out.tags = v.tags;

  if (v.hsn.trim()) out.hsn = v.hsn.trim();
  if (v.unit.trim()) out.unit = v.unit.trim();
  const rp = rupeesToPaise(v.defaultRateRupees);
  if (rp) out.defaultRatePaise = rp;
  if (v.defaultTaxRateBpsStr) {
    const n = Number(v.defaultTaxRateBpsStr);
    if (Number.isFinite(n)) out.defaultTaxRateBps = n;
  }
  if (v.cessRateBpsStr) {
    const n = Number(v.cessRateBpsStr);
    if (Number.isFinite(n)) out.cessRateBps = n;
  }
  const lc = rupeesToPaise(v.lastCostRupees);
  if (lc) out.lastCostPaise = lc;
  const mrp = rupeesToPaise(v.mrpRupees);
  if (mrp) out.mrpPaise = mrp;
  if (v.tdsSectionDefault.trim()) out.tdsSectionDefault = v.tdsSectionDefault.trim();

  if (v.reorderLevel) {
    const n = Number(v.reorderLevel);
    if (Number.isFinite(n)) out.reorderLevel = n;
  }
  if (v.weightGrams) {
    const n = Number(v.weightGrams);
    if (Number.isFinite(n)) out.weightGrams = n;
  }
  if (v.altUnits.length > 0) out.altUnits = v.altUnits;

  if (v.preferredVendorId) out.preferredVendorId = v.preferredVendorId;
  if (v.leadTimeDays) {
    const n = Number(v.leadTimeDays);
    if (Number.isFinite(n)) out.leadTimeDays = n;
  }
  if (v.minOrderQty) {
    const n = Number(v.minOrderQty);
    if (Number.isFinite(n)) out.minOrderQty = n;
  }
  if (v.imageObjectKey.trim()) out.imageObjectKey = v.imageObjectKey.trim();
  return out;
}

/** Build an update payload — only fields that differ from baseline. */
export function valuesToUpdatePayload(
  v: ItemFormValues,
  base: ItemFormValues,
  id: string,
): z.infer<typeof items.updateInput> {
  const patch: z.infer<typeof items.updateInput> = { id };

  const stringDiff = <K extends keyof ItemFormValues>(key: K) => {
    const next = (v[key] as string).trim();
    const prev = (base[key] as string).trim();
    if (next !== prev) (patch as unknown as Record<string, string>)[key as string] = next;
  };

  stringDiff("name");
  stringDiff("unit");
  stringDiff("hsn");
  stringDiff("sku");
  stringDiff("description");
  stringDiff("category");
  stringDiff("brand");
  stringDiff("manufacturer");
  stringDiff("barcode");
  stringDiff("vendorSku");
  stringDiff("tdsSectionDefault");
  stringDiff("imageObjectKey");

  if (v.itemType !== base.itemType) patch.itemType = v.itemType;
  if (v.usage !== base.usage) patch.usage = v.usage;
  if (v.taxPreference !== base.taxPreference) patch.taxPreference = v.taxPreference;
  if (v.trackStock !== base.trackStock) patch.trackStock = v.trackStock;

  if (JSON.stringify(v.tags) !== JSON.stringify(base.tags)) patch.tags = v.tags;
  if (JSON.stringify(v.altUnits) !== JSON.stringify(base.altUnits)) patch.altUnits = v.altUnits;

  const rateNext = rupeesToPaise(v.defaultRateRupees);
  const ratePrev = rupeesToPaise(base.defaultRateRupees);
  if (rateNext !== ratePrev) {
    (patch as { defaultRatePaise?: string }).defaultRatePaise = rateNext;
  }
  const costNext = rupeesToPaise(v.lastCostRupees);
  const costPrev = rupeesToPaise(base.lastCostRupees);
  if (costNext !== costPrev) {
    (patch as { lastCostPaise?: string }).lastCostPaise = costNext;
  }
  const mrpNext = rupeesToPaise(v.mrpRupees);
  const mrpPrev = rupeesToPaise(base.mrpRupees);
  if (mrpNext !== mrpPrev) {
    (patch as { mrpPaise?: string }).mrpPaise = mrpNext;
  }

  const intDiff = <K extends keyof ItemFormValues>(
    key: K,
    wireKey: keyof z.infer<typeof items.updateInput>,
  ) => {
    if (v[key] === base[key]) return;
    const raw = v[key] as string;
    if (raw === "") return; // V1.0.x doesn't clear nullable ints via patch
    const n = Number(raw);
    if (Number.isFinite(n)) (patch as unknown as Record<string, number>)[wireKey] = n;
  };

  intDiff("defaultTaxRateBpsStr", "defaultTaxRateBps");
  intDiff("cessRateBpsStr", "cessRateBps");
  intDiff("reorderLevel", "reorderLevel");
  intDiff("weightGrams", "weightGrams");
  intDiff("leadTimeDays", "leadTimeDays");
  intDiff("minOrderQty", "minOrderQty");

  if (v.preferredVendorId !== base.preferredVendorId) {
    (patch as { preferredVendorId?: string }).preferredVendorId = v.preferredVendorId;
  }

  return patch;
}

// ── helpers ──────────────────────────────────────────────────────

function paiseToRupees(paise: string | null): string {
  if (paise === null || paise === "") return "";
  const rupees = Number(BigInt(paise)) / 100;
  return rupees.toFixed(2);
}

function rupeesToPaise(rupees: string): string {
  const cleaned = rupees.replace(/[,\s]/g, "").trim();
  if (cleaned === "") return "";
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return "";
  const [whole, frac = ""] = cleaned.split(".");
  const fracPadded = (frac + "00").slice(0, 2);
  return (BigInt(whole!) * 100n + BigInt(fracPadded || "0")).toString();
}
